<template>
    <div class="modals" v-if="active">
        <form-modal v-model="modals.editIssue"
            size="xl"
            :title="$t('common.issues')"
            :loading="issueForm.loading"
            :scroll-reset="false"
            :animationDuration="0"
            :centered="false"
        >
            <new-existing-tabs>
                <template v-slot:new>
                    <issue-form v-model="issueForm.data"
                        :errors="issueForm.errors"
                        :modal="modals.editIssue"
                        :timeslot="timeslot"
                    >
                    </issue-form>
                </template>
                <template v-slot:existing>
                    <issues ref="issues"
                        :modal="modals.editIssue"
                        :registration="registration"
                        :simulation="simulation"
                        :session="session"
                        :timeslot="timeslot"
                        @update="updateIssue"
                        @remove="removeIssue"
                    >
                    </issues>
                </template>
            </new-existing-tabs>
        </form-modal>

        <form-modal v-model="modals.editNote"
            :title="$t('dashboard.comment')"
            :loading="noteForm.loading"
            size="xl"
            :centered="false"
        >
            <note-form v-model="noteForm.data"
                :errors="noteForm.errors"
                :modal="modals.editNote"
                :features="features"
            >
            </note-form>
        </form-modal>

        <form-modal v-model="modals.editPicture"
            :title="$t('dashboard.drawing')"
            :loading="pictureForm.loading"
            size="xl"
            :centered="false"
        >
            <picture-file-form v-model="pictureForm.data"
                :errors="pictureForm.errors"
                :modal="modals.editPicture"
                :features="features"
                :endpoint="feedbackEndpoint"
                @change="refresh"
            >
            </picture-file-form>
        </form-modal>

        <form-modal v-model="modals.updateIssue"
            :title="$t('common.issue')"
            :loading="issueFormForUpdate.loading"
            size="xl"
            :centered="false"
        >
            <issue-form v-model="issueFormForUpdate.data"
                :errors="issueFormForUpdate.errors"
                :modal="modals.updateIssue"
                :timeslot="timeslot"
            >
            </issue-form>
        </form-modal>

        <confirm-modal v-model="modals.confirmDeleteIssue">
            <p>{{ $t('issues.delete-confirm') }}</p>
        </confirm-modal>

    </div>
</template>

<script>
    import CrudForm from '@trax/classes/CrudForm'
    import CrudClient from '@trax/classes/CrudClient'
    import NewExistingTabs from './NewExistingTabs'
    import NoteForm from './NoteForm'
    import PictureFileForm from './PictureFileForm'
    import IssueForm from './IssueForm'
    import Issues from './Issues'
    import moment from 'moment'

    export default {

        components: {
            NewExistingTabs, NoteForm, PictureFileForm, IssueForm, Issues, //Feedbacks, PictureForm
        },

        props: {

            modals: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },

            features: {
                default: null
            },

            role: {
                type: String,
                default: 'instructor'
            },
        },

        data() {
            const context = this.session ? 'session' : 'log'
            const feedbackEndpoint = '/socrates/api/front/dashboard/data/context/' + context + '/feedbacks'
            const issueEndpoint = '/socrates/api/front/dashboard/data/context/' + context + '/issues'

            let feedbackClient = new CrudClient(feedbackEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            let issueClient = new CrudClient(issueEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            return {
                feedbackEndpoint,
                issueEndpoint,
                feedbackClient,
                issueClient,
                noteForm: new CrudForm(feedbackClient, this.mapNote, this.unmapNote),
                pictureForm: new CrudForm(feedbackClient, this.mapPicture, this.unmapPicture),
                issueForm: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                issueFormForUpdate: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                timeslot: null,
                moment: moment,
            }
        },

        created() {
            this.noteForm.init(this, 'noteForm')
            this.pictureForm.init(this, 'pictureForm')
            this.issueForm.init(this, 'issueForm')
            this.issueFormForUpdate.init(this, 'issueFormForUpdate')
        },

        computed: {

            active() {
                return true
            },
        },

        methods: {
            
            refresh() {
                this.$emit('refresh')
            },

            createOrUpdateNote(timeslot) {
                this.timeslot = timeslot
                this.noteForm.mapData = this.mapNote
                this.noteForm.unmapData = this.unmapNote

                // Force the timeslot_id because as we don't provide any ID,
                // the back-end would not be able to find the related timeslot.
                this.feedbackClient.query.timeslot_id = timeslot.id
                this.feedbackClient.query.type = 'note'

                this.modals.open('editNote')

                // We use an update to force the form to load the existing data for the timeslot (ID == 0)
                this.noteForm.update(0).then(resp => {
                    this.modals.close('editNote')
                    this.refresh()
                })
            },

            createOrUpdatePicture(timeslot) {
                this.timeslot = timeslot
                this.pictureForm.mapData = this.mapPicture
                this.pictureForm.unmapData = this.unmapPicture

                // Force the timeslot_id because as we don't provide any ID,
                // the back-end would not be able to find the related timeslot.
                this.feedbackClient.query.timeslot_id = timeslot.id
                this.feedbackClient.query.type = 'picture'

                this.modals.open('editPicture')

                // We use an update to force the form to load the existing data for the timeslot (ID == 0).
                this.pictureForm.update(0)
                
                // The PictureFileForm will close the modal and send a @change event to do the refresh.
            },

            createIssue(timeslot) {
                this.timeslot = timeslot
                this.issueForm.mapData = this.mapIssue
                this.issueForm.unmapData = this.unmapIssue

                // Do this at the main popup opening.
                this.issueFormForUpdate.mapData = this.mapIssue
                this.issueFormForUpdate.unmapData = this.unmapIssue

                this.modals.open('editIssue')
                this.issueForm.create().then(resp => {
                    this.modals.close('editIssue')
                    this.refresh()
                })
            },

            updateIssue(issue) {
                this.modals.open('updateIssue')
                this.issueFormForUpdate.update(issue).then(resp => {
                    this.modals.close('updateIssue')
                    this.resetModal('editIssue')
                    this.$refs.issues.list()
                    this.refresh()
                })
            },

            removeIssue(issue) {
                this.modals.open('confirmDeleteIssue').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editIssue')
                        this.issueClient.delete(issue.id).then(resp => {
                            this.$refs.issues.list()
                            this.refresh()
                        })
                    }
                })
            },

            resetModal(name) {
                this.modals.close(name)
                this.$nextTick(() => {
                    this.modals.open(name)
                });
            },

            mapPicture(feedback) {
                let data = this.mapFeedback(feedback)
                // We need to add this now because data will be directly sent by the form,
                // without passing by the unmapFeedback method.
                data.type = 'picture'
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = this.timeslot ? this.timeslot.id : null
                data.file_src = feedback ? feedback.file_src : null
                return data
            },

            mapNote(feedback) {
                let data = this.mapFeedback(feedback)
                data.description = feedback ? feedback.description : null
                return data
            },

            mapFeedback(feedback) {
                return {
                    id: feedback ? feedback.id : null,
                    remote_id: feedback ? feedback.remote_id : null,
                }
            },

            mapIssue(issue) {
                return {
                    id: issue ? issue.id : null,
                    remote_id: issue ? issue.remote_id : null,
                    log_time_from: issue ? issue.log_time_from : null,
                    log_time_to: issue ? issue.log_time_to : null,
                    entered_at: issue ? issue.entered_at : null,
                    summary: issue ? issue.summary : null,
                    description: issue ? issue.description : null,
                }
            },

            unmapNote(data) {
                return this.unmapFeedback(data, 'note')
            },

            unmapPicture(data) {
                return this.unmapFeedback(data, 'picture')
            },

            unmapFeedback(data, type) {
                data.type = type
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = this.timeslot ? this.timeslot.id : null
                return data
            },

            unmapIssue(data) {
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = this.timeslot ? this.timeslot.id : null
                data.entered_at = this.moment().format('YYYY-MM-DD HH:mm:ss')
                data.author_role = this.role
                return data
            },
        }
    }
</script>
